import { ALL_ROOMS_PATTERN, findMatchingRoomNames, stripRoomNameNoise } from '../../shared/roomNameMatcher';

import type { ScopeChipColor } from './ScopeCell';

export type ScopeSource = 'explicit' | 'inferred_all' | 'needs_review';

export interface RoomsScopeResult {
  matchedRooms: Array<string>;
  scope: 'all' | 'only' | 'except';
  source: ScopeSource;
}

export interface DaysScopeResult {
  matchedDays: Array<string>;
  source: ScopeSource;
}

// Deliberately narrower than the shared `isAllRoomsName`: this drives stop-sells, so it does not
// inherit the wider `excl|exc` matching the EGS tab needs.
const EXCEPT_PATTERN = /\b(?:except|excluding)\b/i;
const ROOM_TERM_PATTERN = /room|bedroom|studio|\bbr\b|suite|villa|apartment/i;

const DAY_NAMES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_PATTERN = new RegExp(`\\b(${DAY_NAMES.join('|')})s?\\b`, 'gi');
// "Fridayz", "Good Friday", "AFL Weekend" name an event/holiday, not a day-of-week rule.
const DAY_FALSE_POSITIVE_PATTERN = /\bgood\s+friday\b|fridayz/i;

export function classifyRoomsScope(name: string, roomTypeNames: Array<string>): RoomsScopeResult {
  if (ALL_ROOMS_PATTERN.test(name) && !EXCEPT_PATTERN.test(name)) {
    return { matchedRooms: [], scope: 'all', source: 'explicit' };
  }

  const scope = EXCEPT_PATTERN.test(name) ? 'except' : 'only';
  const [, textAfterKeyword] = EXCEPT_PATTERN.test(name) ? name.split(EXCEPT_PATTERN) : [null, name];
  const candidateText = stripRoomNameNoise(textAfterKeyword ?? name);
  const matchedRooms = findMatchingRoomNames(candidateText, roomTypeNames);

  if (matchedRooms.length) {
    return { matchedRooms, scope, source: 'explicit' };
  }

  if (ROOM_TERM_PATTERN.test(name)) {
    return { matchedRooms: [], scope, source: 'needs_review' };
  }

  return { matchedRooms: [], scope: 'all', source: 'inferred_all' };
}

export function classifyDaysScope(name: string): DaysScopeResult {
  if (DAY_FALSE_POSITIVE_PATTERN.test(name)) {
    return { matchedDays: [], source: 'inferred_all' };
  }

  const matches = Array.from(name.matchAll(DAY_PATTERN)).map((match) => match[1].toLowerCase());

  if (!matches.length) {
    return { matchedDays: [], source: 'inferred_all' };
  }

  const matchedDays = Array.from(new Set(matches)).map((day) => day.charAt(0).toUpperCase() + day.slice(1));

  return { matchedDays, source: 'explicit' };
}

export const SCOPE_SOURCE_LABEL: Record<ScopeSource, string> = {
  explicit: 'From name',
  inferred_all: 'Assumed (no restriction stated)',
  needs_review: 'Needs review',
};

export const ROOM_SCOPE_SOURCE_TOOLTIP: Record<ScopeSource, string> = {
  explicit: 'Explicit room name in SF blackout name',
  inferred_all: 'No explicit room name in SF blackout name, assumed to apply to all',
  needs_review: 'Ambiguous room name in SF blackout name',
};

export function getRoomsScopeChipColor(result: RoomsScopeResult): ScopeChipColor {
  if (result.source === 'needs_review') {
    return 'warning';
  }

  if (result.scope === 'except') {
    return 'secondary';
  }

  return result.scope === 'only' ? 'primary' : 'default';
}

export function getDaysScopeChipColor(result: DaysScopeResult): ScopeChipColor {
  return result.source === 'explicit' ? 'info' : 'default';
}

export function formatRoomsScope(result: RoomsScopeResult): string {
  if (result.scope === 'all') {
    return 'All room types';
  }

  if (!result.matchedRooms.length) {
    return 'Unable to determine';
  }

  const roomList = result.matchedRooms.join(', ');
  return result.scope === 'except' ? `All room types except ${roomList}` : roomList;
}

export function formatDaysScope(result: DaysScopeResult): string {
  return result.matchedDays.length ? result.matchedDays.join(', ') : 'All days in range';
}

// Shared by the read-only tab and the apply-plan builder so scope logic stays consistent.
export function blackoutAppliesToRoomType(
  blackoutName: string,
  roomTypeName: string,
  roomTypeNames: Array<string>,
): boolean {
  const rooms = classifyRoomsScope(blackoutName, roomTypeNames);

  if (rooms.source === 'needs_review' || rooms.scope === 'all') {
    return true;
  }

  const isMatchedRoom = rooms.matchedRooms.includes(roomTypeName);
  return rooms.scope === 'except' ? !isMatchedRoom : isMatchedRoom;
}
